import './LdaPage.scss';
import DialogActions from '../../../shared/components/DialogActions/DialogActions';
import DialogContent from '../../../shared/components/DialogContent/DialogContent';
import DialogTitle from '../../../shared/components/DialogTitle/DialogTitle';
import Paper from '../../../shared/components/Paper/Paper';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Alert from '../../../shared/components/Alert/Alert';
import Dialog from '../../../shared/components/Dialog/Dialog';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Box from '../../../shared/components/Box/Box';
import Typography from '../../../shared/components/Typography/Typography';
import { createTask, reassignPendingLdaTasks } from '../../organization/tasks/tasks.api';
import { getOneProject, getResolvedProjectNodes, updateProject } from '../../projects/project.api';
import LdaTreeView from '../components/LdaTreeView';
import LdaPublicationDialog from '../components/LdaPublicationDialog';
import { printLdaDocument } from '../ldaDocumentExport';
import { getAssignableCrmUsers } from '../../crm/crmUsers';
import {
  createProjectLdaLine,
  deleteProjectLdaLine,
  getLdaAssignableUsers,
  getProjectLdaLines,
  replaceProjectLdaLines,
  updateProjectLdaLine,
} from '../lda.api';

const statusOptions = ['A dessiner', 'A valider', 'A diffuser', 'Diffusé'];
const preDepositStatuses = statusOptions.filter((status) => status !== 'Diffusé');
const lockedAfterDepositStatuses = ['A diffuser', 'Diffusé'];

const fallbackUsers = [];

const DOCUMENT_TYPE_FALLBACK_CODES = {
  plan: 'PLN',
  schema: 'SCH',
  note: 'NDC',
};

const normalizeCode = (value, length = 3) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-zA-Z0-9]/g, '')
  .toUpperCase()
  .slice(0, length)
  .padEnd(length, 'X');

const parseCodeGed = (value) => {
  const parts = String(value || '').split('_');

  if (parts.length !== 7) {
    return {};
  }

  const [emitter, phase, projectNumber, lot, documentType, documentNumber, indice] = parts;
  return { emitter, phase, projectNumber, lot, documentType, documentNumber, indice };
};

const parseLdaId = (value) => {
  const match = String(value || '').match(/^lda-([A-Z]{3})-([A-Z]{3})-(\d{3})/i);

  if (!match) {
    return {};
  }

  return {
    phase: match[1].toUpperCase(),
    documentType: match[2].toUpperCase(),
    documentNumber: match[3],
  };
};

const getProjectCodeFallback = (projectName, projectId) => (
  normalizeCode(projectName, 3) || normalizeCode(`Projet ${projectId}`, 3)
);

const getDocumentTypeFallback = (row) => {
  const label = String(row.document_type || row.title || '').toLowerCase();
  return DOCUMENT_TYPE_FALLBACK_CODES[label] || normalizeCode(label || 'DOC');
};

const getCodeParts = (row, fallback = {}) => {
  const parsedCodeGed = parseCodeGed(row.codeGed || row.code_ged || row.title);
  const parsedId = parseLdaId(row.rootId || row.id);
  const fallbackDocumentNumber = Number.isInteger(fallback.rowIndex)
    ? String(fallback.rowIndex + 1).padStart(3, '0')
    : '001';

  return {
    emitter: row.emitter || row.emitter_code || parsedCodeGed.emitter || 'ICC',
    phase: row.phase || row.phase_code || parsedCodeGed.phase || parsedId.phase || 'EXE',
    projectNumber: row.projectNumber || row.project_number || parsedCodeGed.projectNumber || getProjectCodeFallback(fallback.projectName, fallback.projectId),
    lot: row.lot || row.lot_code || parsedCodeGed.lot || 'CFO',
    documentType: row.document_type_code || row.document_type || parsedCodeGed.documentType || parsedId.documentType || getDocumentTypeFallback(row),
    documentNumber: row.documentNumber || row.document_number || parsedCodeGed.documentNumber || parsedId.documentNumber || fallbackDocumentNumber,
    indice: row.indice || parsedCodeGed.indice || 'A',
  };
};

const buildCodeGed = (row, indice = row.indice, fallback = {}) => {
  const codeParts = getCodeParts(row, fallback);

  return [
    codeParts.emitter,
    codeParts.phase,
    codeParts.projectNumber,
    codeParts.lot,
    codeParts.documentType,
    codeParts.documentNumber,
    indice || codeParts.indice,
  ].filter(Boolean).join('_');
};

const normalizeLdaRow = (row, fallback = {}) => {
  const codeParts = getCodeParts(row, fallback);
  const codeGed = row.codeGed || row.code_ged || buildCodeGed(row, codeParts.indice, fallback);
  const title = row.title || row.denomination || '';
  const titleLooksLikeCode = title && title === codeGed;
  const rootId = row.rootId || row.root_id || row.id;

  return {
    ...row,
    ...codeParts,
    rootId,
    codeGed,
    type: row.documentCategory || row.document_category || '',
    title: titleLooksLikeCode ? [row.document_type, row.observations].filter(Boolean).join(' - ') : title,
  };
};

const formatDate = (value) => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString('fr-FR');
};

const getTodayDate = () => new Date().toISOString().slice(0, 10);

const getAvailableStatusOptions = (row) => {
  const options = row.documentId ? lockedAfterDepositStatuses : preDepositStatuses;

  return options.includes(row.status) ? options : [row.status, ...options];
};

const getStatusClass = (status) => status
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/\s+/g, '-');

const isDiffusedStatus = (status) => getStatusClass(status) === 'diffuse';

const calculateProjectProgressFromLda = (rows) => {
  const deliverables = rows.filter((row) => !row.parentId);

  if (!deliverables.length) {
    return 0;
  }

  const diffusedCount = deliverables.filter((row) => isDiffusedStatus(row.status)).length;
  return Math.round((diffusedCount / deliverables.length) * 100);
};

const hasGedDocument = (row) => Boolean(row.documentId || row.document_id || row.visaDocumentId || row.visa_document_id);

const getOperationalAssigneeValue = (row) => String(row?.assignedUserId || row?.assigned_user_id || row?.attribution || '');
const getPlannedResourceValue = (row) => String(row?.plannedResourceUserId || row?.planned_resource_user_id || '');
const getPlannedResourceName = (row) => row?.plannedResourceName || row?.planned_resource_name || '';

const getUserLabel = (user) => (
  user.name
  || [user.firstName || user.first_name, user.lastName || user.last_name].filter(Boolean).join(' ')
  || user.email
  || String(user.id)
);

const parseHours = (value) => {
  const hours = Number.parseFloat(String(value || '').replace(',', '.'));
  return Number.isNaN(hours) ? 0 : hours;
};

const getLdaStartDate = (row) => row.forecastStartDate || row.forecast_start_date || null;
const getLdaDeliveryDate = (row) => row.deliveryDate || row.delivery_date || row.forecast_delivery_date || null;


const readStoredLdaRows = (projectId) => {
  if (!projectId || typeof window === 'undefined') {
    return [];
  }

  try {
    const rows = JSON.parse(window.localStorage.getItem(`sas-erp-project-${projectId}-lda`) || '[]');
    return Array.isArray(rows) ? rows : [];
  } catch (error) {
    return [];
  }
};

const LDA_HEADERS = ['Document', 'Statut', 'Observations', 'Echeance', 'Ressources', 'Actions'];

const getNextDocumentNumber = (rows) => String(rows
  .filter((row) => !row.parentId)
  .reduce((max, row) => {
    const number = Number.parseInt(getCodeParts(row).documentNumber, 10);
    return Number.isNaN(number) ? max : Math.max(max, number);
  }, 0) + 1).padStart(3, '0');

const createLdaDraft = (projectId, rows, projectName) => {
  const documentNumber = getNextDocumentNumber(rows);

  return {
    emitter: 'ICC',
    phase: 'EXE',
    projectNumber: getProjectCodeFallback(projectName, projectId),
    lot: 'CFO',
    documentType: 'PLN',
    documentNumber,
    title: '',
    indice: '0',
    observations: '',
    status: 'A dessiner',
    realizationTime: '',
    forecastStartDate: '',
    deliveryDate: '',
    diffusionDate: '',
    plannedResourceUserId: '',
    plannedResourceName: '',
    plannedResourceManual: false,
  };
};

const getDraftFromRow = (row) => {
  const codeParts = getCodeParts(row);

  return {
    emitter: codeParts.emitter,
    phase: codeParts.phase,
    projectNumber: codeParts.projectNumber,
    lot: codeParts.lot,
    documentType: codeParts.documentType,
    documentNumber: codeParts.documentNumber,
    title: row.title || '',
    type: row.documentCategory || row.document_category || '',
    indice: codeParts.indice,
    observations: row.observations || '',
    status: row.status || 'A dessiner',
    realizationTime: row.realizationTime || row.realization_time_hours || '',
    forecastStartDate: row.forecastStartDate || row.forecast_start_date || '',
    deliveryDate: row.deliveryDate || row.forecast_delivery_date || '',
    diffusionDate: row.diffusionDate || row.diffusion_date || '',
    plannedResourceUserId: getPlannedResourceValue(row),
    plannedResourceName: getPlannedResourceName(row),
    plannedResourceManual: Boolean(row.plannedResourceManual ?? row.planned_resource_manual),
  };
};

function LdaPage() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [ldaLoading, setLdaLoading] = useState(true);
  const [ldaError, setLdaError] = useState(null);
  const [projectName, setProjectName] = useState(`Projet ${projectId}`);
  const [project, setProject] = useState({ id: projectId, name: `Projet ${projectId}` });
  const [resolvedNodes, setResolvedNodes] = useState([]);
  const [users, setUsers] = useState(fallbackUsers);
  const [usersError, setUsersError] = useState(null);
  const [taskError, setTaskError] = useState(null);
  const [progressSyncError, setProgressSyncError] = useState(null);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [lineDialog, setLineDialog] = useState(null);
  const [assignmentDialog, setAssignmentDialog] = useState(null);
  const [publicationRow, setPublicationRow] = useState(null);
  const [printError, setPrintError] = useState(null);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    const fetchProject = async () => {
      if (!projectId) return;

      try {
        const [projectData, nodeData] = await Promise.all([getOneProject(projectId), getResolvedProjectNodes(projectId)]);
        setProject(projectData || { id: projectId, name: `Projet ${projectId}` });
        setResolvedNodes(Array.isArray(nodeData) ? nodeData : []);
        setProjectName(projectData?.name || projectData?.label || `Projet ${projectId}`);
      } catch (error) {
        setProject({ id: projectId, name: `Projet ${projectId}` });
        setResolvedNodes([]);
        setProjectName(`Projet ${projectId}`);
      }
    };

    fetchProject();
  }, [projectId]);

  useEffect(() => {
    const fetchLdaRows = async () => {
      if (!projectId) {
        setRows([]);
        setLdaLoading(false);
        return;
      }

      try {
        setLdaLoading(true);
        let data = await getProjectLdaLines(projectId);
        const storedRows = readStoredLdaRows(projectId);

        if ((!Array.isArray(data) || !data.length) && storedRows.length) {
          data = await replaceProjectLdaLines(projectId, storedRows);
          window.localStorage.removeItem(`sas-erp-project-${projectId}-lda`);
        }

        const nextRows = Array.isArray(data) ? data.map((row, index) => normalizeLdaRow(row, { projectId, projectName, rowIndex: index })) : [];
        setRows(nextRows);
        setLdaError(null);
      } catch (error) {
        setRows([]);
        setLdaError(error.message || 'Impossible de charger la LDA depuis la base de donnees.');
      } finally {
        setLdaLoading(false);
      }
    };

    fetchLdaRows();
  }, [projectId, projectName]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const data = await getLdaAssignableUsers();
        const userList = Array.isArray(data) ? data : data.data || [];
        setUsers(getAssignableCrmUsers(userList));
        setUsersError(null);
      } catch (error) {
        setUsers(fallbackUsers);
        setUsersError('Impossible de charger les utilisateurs, liste locale utilisée.');
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    if (!projectId || ldaLoading) return undefined;

    const progressPercent = calculateProjectProgressFromLda(rows);
    const syncTimeout = window.setTimeout(async () => {
      try {
        await updateProject(projectId, { progressPercent });
        setProgressSyncError(null);
      } catch (error) {
        setProgressSyncError('Impossible de synchroniser l avancement du projet.');
      }
    }, 300);

    return () => window.clearTimeout(syncTimeout);
  }, [ldaLoading, projectId, rows]);

  const persistRowChanges = async (rowId, changes) => {
    try {
      await updateProjectLdaLine(projectId, rowId, changes);
      setLdaError(null);
    } catch (error) {
      setLdaError(error.message || 'Impossible de sauvegarder la ligne LDA.');
    }
  };

  const updateRowField = (rowId, field, value) => {
    setRows((currentRows) => currentRows.map((row) => (
      row.id === rowId ? { ...row, [field]: value } : row
    )));
    persistRowChanges(rowId, { [field]: value });
  };

  const updateStatus = async (rowId, status) => {
    const currentRow = rows.find((row) => row.id === rowId);

    if (!currentRow) {
      return;
    }

    const diffusionDate = isDiffusedStatus(status) && !currentRow.diffusionDate ? getTodayDate() : currentRow.diffusionDate;
    const rowChanges = { status, diffusionDate };

    setRows((currentRows) => currentRows.map((row) => (
      row.id === rowId ? { ...row, ...rowChanges } : row
    )));

    try {
      await updateProjectLdaLine(projectId, rowId, rowChanges);

      if (isDiffusedStatus(status) && currentRow.documentId) {
        const refreshedRows = await getProjectLdaLines(projectId);
        setRows(Array.isArray(refreshedRows)
          ? refreshedRows.map((row, index) => normalizeLdaRow(row, { projectId, projectName, rowIndex: index }))
          : []);
      }

      setLdaError(null);
    } catch (error) {
      setLdaError(error.message || 'Impossible de sauvegarder le statut LDA.');
    }
  };

  const handleStatusChange = (row, status) => {
    if (row.status === 'A dessiner' && status === 'A valider') {
      setPendingStatusChange({ rowId: row.id, status });
      return;
    }

    updateStatus(row.id, status);
  };

  const confirmStatusChange = () => {
    if (pendingStatusChange) {
      updateStatus(pendingStatusChange.rowId, pendingStatusChange.status);
      setPendingStatusChange(null);
    }
  };

  const rejectStatusChange = () => {
    setPendingStatusChange(null);
    navigate('/Wiki');
  };

  const createLdaAssignmentTask = async (row, assignedUserId) => {
    if (!assignedUserId) return;

    await createTask({
      title: `${projectName} - ${row.title}`,
      description: `Document LDA "${row.codeGed || row.title}" indice ${row.indice} confié pour réalisation.`,
      status: 'todo',
      estimatedHours: parseHours(row.realizationTime),
      remainingHours: parseHours(row.realizationTime),
      startDate: getLdaStartDate(row) || getTodayDate(),
      dueDate: getLdaDeliveryDate(row),
      assignedUserId,
      projectId,
      ldaLineId: row.id,
      timeTrackingEnabled: true,
    });
  };

  const handlePlannedResourceChange = async (row, plannedResourceUserId) => {
    const user = users.find((candidate) => String(candidate.id) === String(plannedResourceUserId));
    const changes = {
      plannedResourceUserId: plannedResourceUserId || null,
      plannedResourceName: user ? getUserLabel(user) : null,
      plannedResourceManual: true,
    };

    setRows((currentRows) => currentRows.map((currentRow) => (
      currentRow.id === row.id ? { ...currentRow, ...changes } : currentRow
    )));
    await persistRowChanges(row.id, changes);
  };

  const openAssignmentDialog = (row) => {
    setAssignmentDialog({
      row,
      assignedUserId: getOperationalAssigneeValue(row) || getPlannedResourceValue(row) || '',
    });
  };

  const closeAssignmentDialog = () => setAssignmentDialog(null);

  const confirmOperationalAssignment = async () => {
    if (!assignmentDialog?.row || !assignmentDialog.assignedUserId) return;

    const row = assignmentDialog.row;
    const assignedUserId = assignmentDialog.assignedUserId;

    try {
      await updateProjectLdaLine(projectId, row.id, { attribution: assignedUserId, assignedUserId });
      const result = await reassignPendingLdaTasks(
        row.id,
        projectId,
        row.codeGed || row.code_ged,
        assignedUserId,
        {
          estimatedHours: parseHours(row.realizationTime),
          startDate: getLdaStartDate(row),
          dueDate: getLdaDeliveryDate(row),
        }
      );

      if (!result?.updatedCount) {
        await createLdaAssignmentTask({ ...row, attribution: assignedUserId, assignedUserId }, assignedUserId);
      }

      setRows((currentRows) => currentRows.map((currentRow) => (
        currentRow.id === row.id ? { ...currentRow, attribution: assignedUserId, assignedUserId } : currentRow
      )));
      setTaskError(null);
      closeAssignmentDialog();
    } catch (error) {
      setTaskError(error.message || 'Le livrable n\'a pas pu être confié.');
    }
  };

  const openAddLineDialog = () => {
    setLineDialog({
      mode: 'create',
      rowId: null,
      draft: createLdaDraft(projectId, rows, projectName),
    });
  };

  const openEditLineDialog = (row) => {
    setLineDialog({
      mode: 'edit',
      rowId: row.id,
      draft: getDraftFromRow(row),
    });
  };

  const getLineFamily = (line, sourceRows = rows) => {
    const rootId = line.parentId ? line.rootId : line.id;
    return sourceRows.filter((row) => row.id === rootId || row.rootId === rootId);
  };

  const canDeleteLine = (line) => {
    const linesToCheck = line.parentId ? [line] : getLineFamily(line);
    return linesToCheck.every((row) => !hasGedDocument(row));
  };

  const deleteLine = async (line) => {
    if (!canDeleteLine(line)) {
      return;
    }

    try {
      await deleteProjectLdaLine(projectId, line.id);

      setRows((currentRows) => {
        if (line.parentId) {
          return currentRows.filter((row) => row.id !== line.id);
        }

        return currentRows.filter((row) => row.id !== line.id && row.rootId !== line.id);
      });

      setLdaError(null);
    } catch (error) {
      setLdaError(error.message || 'Impossible de supprimer la ligne LDA.');
    }
  };

  const closeLineDialog = () => {
    setLineDialog(null);
  };

  const updateLineDraft = (field, value) => {
    setLineDialog((current) => (
      current
        ? { ...current, draft: { ...current.draft, [field]: value } }
        : current
    ));
  };

  const getLineStatusOptions = (status) => {
    const options = [...statusOptions, 'A verifier'];
    return options.includes(status) ? options : [status, ...options];
  };

  const saveLineDialog = async () => {
    if (!lineDialog) {
      return;
    }

    const draft = {
      ...lineDialog.draft,
      emitter: normalizeCode(lineDialog.draft.emitter),
      phase: normalizeCode(lineDialog.draft.phase),
      projectNumber: normalizeCode(lineDialog.draft.projectNumber || getProjectCodeFallback(projectName, projectId), 3),
      lot: normalizeCode(lineDialog.draft.lot),
      documentType: normalizeCode(lineDialog.draft.documentType),
      documentNumber: String(lineDialog.draft.documentNumber || getNextDocumentNumber(rows)).padStart(3, '0').slice(-3),
      indice: String(lineDialog.draft.indice || 'A').toUpperCase(),
      title: lineDialog.draft.title || 'Nouveau livrable',
    };
    const nextCodeGed = buildCodeGed(draft, draft.indice, { projectId, projectName });

    try {
      if (lineDialog.mode === 'create') {
        const createdRow = await createProjectLdaLine(projectId, {
          ...draft,
          parentId: null,
          rootId: null,
          codeGed: nextCodeGed,
          visaDocumentId: null,
          workingDocumentId: null,
          validationDocumentId: null,
          documentId: null,
        });
        const normalizedCreatedRow = normalizeLdaRow(createdRow, { projectId, projectName, rowIndex: rows.length });

        setRows((currentRows) => [...currentRows, normalizedCreatedRow]);

      } else {
        const updatedRow = await updateProjectLdaLine(projectId, lineDialog.rowId, {
          ...draft,
          codeGed: nextCodeGed,
        });
        const normalizedUpdatedRow = normalizeLdaRow(updatedRow, { projectId, projectName });



        setRows((currentRows) => currentRows.map((row) => (
          row.id === lineDialog.rowId ? normalizedUpdatedRow : row
        )));
      }

      setLdaError(null);
      closeLineDialog();
    } catch (error) {
      setLdaError(error.message || 'Impossible de sauvegarder la ligne LDA.');
    }
  };

  const renderVisaLink = (row) => {
    if (!isDiffusedStatus(row.status)) {
      return null;
    }

    if (row.visaDocumentId) {
      return (
        <Link className="lda-deposit-link" to={`/GED?documentId=${row.visaDocumentId}&mode=view`}>
          Visualiser VISA
        </Link>
      );
    }

    return (
      <Link className="lda-deposit-link" to={`/GED?projectId=${projectId}&ldaLineId=${row.id}&mode=publish-visa`}>
        VISA en attente
      </Link>
    );
  };

  const renderDepositLink = (row) => {
    const status = getStatusClass(row.status);
    const workingDocumentId = row.workingDocumentId || row.working_document_id;
    const validationDocumentId = row.validationDocumentId || row.validation_document_id;
    const finalDocumentId = row.documentId || row.document_id;

    if (status === 'a-dessiner') {
      return (
        <button className="lda-deposit-link" type="button" onClick={() => setPublicationRow(row)}>
          {workingDocumentId ? 'Mettre à jour travail' : 'Publier fichier de travail'}
        </button>
      );
    }

    if (status === 'a-valider') {
      return (
        <button className="lda-deposit-link" type="button" onClick={() => setPublicationRow(row)}>
          {validationDocumentId ? 'Remplacer validation' : 'Publier pour validation'}
        </button>
      );
    }

    if (status === 'a-diffuser') {
      return (
        <button className="lda-deposit-link" type="button" onClick={() => setPublicationRow(row)}>
          {finalDocumentId ? 'Remplacer livrable' : 'Publier livrable'}
        </button>
      );
    }

    if (status === 'diffuse' && finalDocumentId) {
      return (
        <Link className="lda-deposit-link" to={`/GED?documentId=${finalDocumentId}&mode=view`}>
          Visualiser GED
        </Link>
      );
    }

    return null;
  };

  const handlePrintLda = async () => {
    try {
      setPrinting(true);
      setPrintError(null);
      await printLdaDocument({ project, rows });
    } catch (error) {
      setPrintError(error.message || 'Impossible de préparer l’impression de la LDA.');
    } finally {
      setPrinting(false);
    }
  };

  const getUserNameById = (userId) => {
    if (!userId) return '';
    const user = users.find((candidate) => String(candidate.id) === String(userId));
    return user ? getUserLabel(user) : String(userId);
  };

  const renderDeliverableRow = (row, { depth, revision }) => {
    const codeParts = getCodeParts(row);
    return (
      <div key={row.id} className={`lda-grid__row lda-grid__row--tree ${revision ? 'lda-grid__row--child' : ''}`} role="row" style={{ '--deliverable-depth': depth }}>
        <div className="lda-grid__cell lda-grid__cell--document" role="cell">
          <div className="lda-title-cell">{revision && <span className="lda-child-badge">{codeParts.indice}</span>}<strong>{row.title || '-'}</strong></div>
          <code className="lda-code">{row.codeGed || buildCodeGed(row)}</code>
          <div className="lda-meta-list"><span>{codeParts.documentType}</span><span>{codeParts.phase}</span><span>Lot {codeParts.lot}</span>
            <span>N° {codeParts.documentNumber}</span><span>Indice {codeParts.indice}</span></div>
        </div>
        <div className="lda-grid__cell" role="cell">
          <select className={`lda-status-select lda-status-select--${getStatusClass(row.status)}`} value={row.status}
            disabled={isDiffusedStatus(row.status)} onChange={(event) => handleStatusChange(row, event.target.value)}>
            {getAvailableStatusOptions(row).map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </div>
        <div className="lda-grid__cell" role="cell"><input className="lda-inline-input" value={row.observations || ''} disabled
          onChange={(event) => updateRowField(row.id, 'observations', event.target.value)} /></div>
        <div className="lda-grid__cell lda-grid__cell--dates" role="cell"><span><small>Début</small>{formatDate(row.forecastStartDate)}</span><span><small>Livraison</small>{formatDate(row.deliveryDate)}</span>
          <span><small>Diffusion</small>{formatDate(row.diffusionDate)}</span><span><small>Temps</small>{row.realizationTime || '-'}</span></div>
        <div className="lda-grid__cell lda-grid__cell--resources" role="cell">
          <div className="lda-resource-block">
            <small>Prévisionnelle</small>
            <select
              className="lda-inline-select"
              value={getPlannedResourceValue(row)}
              disabled={isDiffusedStatus(row.status)}
              onChange={(event) => handlePlannedResourceChange(row, event.target.value)}
            >
              <option value="">Aucune ressource</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>{getUserLabel(user)}</option>
              ))}
            </select>
          </div>
          <div className="lda-resource-block lda-resource-block--operational">
            <small>Opérationnelle</small>
            {getOperationalAssigneeValue(row) ? (
              <span className="lda-resource-assignee">{getUserNameById(getOperationalAssigneeValue(row))}</span>
            ) : (
              <span className="lda-resource-assignee lda-resource-assignee--empty">Non lancée</span>
            )}
            {!isDiffusedStatus(row.status) && (
              <button className="basic-button lda-confier-button" type="button" onClick={() => openAssignmentDialog(row)}>
                {getOperationalAssigneeValue(row) ? 'Réaffecter' : 'Confier'}
              </button>
            )}
          </div>
        </div>
        <div className="lda-grid__cell lda-grid__cell--actions" role="cell">{renderDepositLink(row)}{renderVisaLink(row)}
          <button className="lda-icon-button" type="button" onClick={() => openEditLineDialog(row)} title="Modifier le livrable"
            aria-label={`Modifier ${row.title || row.codeGed || 'le livrable'}`}><EditIcon fontSize="small" /></button>
          {canDeleteLine(row) && <button className="lda-icon-button lda-icon-button--danger" type="button" onClick={() => deleteLine(row)}
            title="Supprimer le livrable" aria-label={`Supprimer ${row.title || row.codeGed || 'le livrable'}`}><DeleteIcon fontSize="small" /></button>}
        </div>
      </div>
    );
  };

  return (
    <Box className="page-container module-page lda-page">
      <div className="project-list-header">
        <div>
          <Typography variant="h4">LDA projet {projectId}</Typography>
          <Typography variant="body2" color="textSecondary">
            Liste des documents attendus et suivi des indices.
          </Typography>
        </div>
        <div className="lda-page-actions">
          <button className="basic-button active lda-add-button" type="button" onClick={openAddLineDialog}>
            Ajouter un livrable
          </button>
          <button className="basic-button" type="button" onClick={handlePrintLda} disabled={printing}>
            {printing ? 'Préparation…' : 'Imprimer la LDA'}
          </button>
          <Link className="basic-button" to={`/projets/${projectId}`}>
            Retour au projet
          </Link>
        </div>
      </div>

      {usersError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {usersError}
        </Alert>
      )}

      {taskError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {taskError}
        </Alert>
      )}

      {ldaError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {ldaError}
        </Alert>
      )}

      {progressSyncError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {progressSyncError}
        </Alert>
      )}

      {printError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {printError}
        </Alert>
      )}

      <Paper className="lda-grid-panel">
        <div className="lda-grid" role="grid" aria-label="LDA projet">
          <div className="lda-grid__header" role="row">
            {LDA_HEADERS.map((header) => (
              <div key={header} className="lda-grid__header-cell" role="columnheader">
                {header}
              </div>
            ))}
          </div>

          <LdaTreeView project={project} resolvedNodes={resolvedNodes} rows={rows} loading={ldaLoading} renderDeliverable={renderDeliverableRow} />

          {/* Ancienne grille supprimée du rendu après migration vers LdaTreeView.
            {ldaLoading ? (
              <div className="loading-state">
                <CircularProgress size={24} />
              </div>
            ) : visibleRows.length ? visibleRows.map((row) => {
              const codeParts = getCodeParts(row);

              return (
                <div key={row.id} className={`lda-grid__row ${row.parentId ? 'lda-grid__row--child' : ''}`} role="row">
                  <div className="lda-grid__cell lda-grid__cell--document" role="cell">
                    <div className="lda-title-cell" style={{ paddingLeft: row.treeLevel ? 28 : 0 }}>
                      {!row.parentId && row.hasChildren && (
                        <button
                          className="lda-tree-toggle"
                          type="button"
                          onClick={() => toggleExpanded(row.id)}
                          title={expandedRows.has(row.id) ? 'Replier' : 'Déplier'}
                        >
                          {expandedRows.has(row.id) ? '-' : '+'}
                        </button>
                      )}
                      {row.parentId && <span className="lda-child-badge">{codeParts.indice}</span>}
                      <strong>{row.title || '-'}</strong>
                    </div>
                    <code className="lda-code">{row.codeGed || buildCodeGed(row)}</code>
                    <div className="lda-meta-list">
                      <span>{codeParts.emitter}</span>
                      <span>{codeParts.phase}</span>
                      <span>Projet {codeParts.projectNumber}</span>
                      <span>Lot {codeParts.lot}</span>
                      <span>{codeParts.documentType}</span>
                      <span>N° {codeParts.documentNumber}</span>
                      <span>Indice {codeParts.indice}</span>
                    </div>
                  </div>

                  <div className="lda-grid__cell" role="cell">
                    <select
                      className={`lda-status-select lda-status-select--${getStatusClass(row.status)}`}
                      value={row.status}
                      disabled={isDiffusedStatus(row.status)}
                      onChange={(event) => handleStatusChange(row, event.target.value)}
                    >
                      {getAvailableStatusOptions(row).map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="lda-grid__cell" role="cell">
                    <input
                      className="lda-inline-input"
                      value={row.observations || ''}
                      onChange={(event) => updateRowField(row.id, 'observations', event.target.value)}
                      disabled
                    />
                  </div>

                  <div className="lda-grid__cell lda-grid__cell--dates" role="cell">
                    <span>
                      <small>Livraison</small>
                      {formatDate(row.deliveryDate)}
                    </span>
                    <span>
                      <small>Diffusion</small>
                      {formatDate(row.diffusionDate)}
                    </span>
                    <span>
                      <small>Temps</small>
                      {row.realizationTime || '-'}
                    </span>
                  </div>

                  <div className="lda-grid__cell" role="cell">
                    <select
                      className="lda-inline-select"
                      value={row.attribution || ''}
                      disabled={isDiffusedStatus(row.status) || hasAttribution(row)}
                      onChange={(event) => handleAttributionChange(row, event.target.value)}
                    >
                      <option value="">Non attribuée</option>
                      {users.map((user) => {
                        const userId = user.id || user.email || getUserLabel(user);
                        return (
                          <option key={userId} value={userId}>
                            {getUserLabel(user)}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div className="lda-grid__cell lda-grid__cell--actions" role="cell">
                    {renderDepositLink(row)}
                    {renderVisaLink(row)}
                    <button
                      className="lda-icon-button"
                      type="button"
                      onClick={() => openEditLineDialog(row)}
                      title="Modifier le livrable"
                      aria-label={`Modifier ${row.title || row.codeGed || 'le livrable'}`}
                    >
                      <EditIcon fontSize="small" />
                    </button>
                    {canDeleteLine(row) && (
                      <button
                        className="lda-icon-button lda-icon-button--danger"
                        type="button"
                        onClick={() => deleteLine(row)}
                        title="Supprimer le livrable"
                        aria-label={`Supprimer ${row.title || row.codeGed || 'le livrable'}`}
                      >
                        <DeleteIcon fontSize="small" />
                      </button>
                    )}
                  </div>
                </div>
              );
            }) : (
              <div className="time-entry-empty">
                Aucune ligne LDA pour ce projet.
              </div>
            )}
          */}
        </div>
      </Paper>


      <LdaPublicationDialog
        open={Boolean(publicationRow)}
        row={publicationRow}
        projectId={projectId}
        onClose={() => setPublicationRow(null)}
        onPublished={(result) => {
          const field = result.stage === 'working'
            ? 'workingDocumentId'
            : (result.stage === 'validation' ? 'validationDocumentId' : 'documentId');
          setRows((currentRows) => currentRows.map((row) => (
            row.id === result.ldaLineId ? { ...row, [field]: result.documentId } : row
          )));
        }}
      />

      <Dialog open={Boolean(assignmentDialog)} onClose={closeAssignmentDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {getOperationalAssigneeValue(assignmentDialog?.row) ? 'Réaffecter le livrable' : 'Confier le livrable'}
        </DialogTitle>
        <DialogContent>
          {assignmentDialog && (
            <div className="lda-assignment-dialog">
              <p>
                Cette action crée ou réaffecte la tâche opérationnelle liée au livrable.
                La ressource prévisionnelle reste une donnée de planification indépendante.
              </p>
              <label>
                Responsable opérationnel
                <select
                  className="lda-inline-select"
                  value={assignmentDialog.assignedUserId}
                  onChange={(event) => setAssignmentDialog((current) => (
                    current ? { ...current, assignedUserId: event.target.value } : current
                  ))}
                >
                  <option value="">Sélectionner un utilisateur</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>{getUserLabel(user)}</option>
                  ))}
                </select>
              </label>
              {getPlannedResourceValue(assignmentDialog.row) && (
                <small>
                  Ressource prévue : {getUserNameById(getPlannedResourceValue(assignmentDialog.row))}
                </small>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <button className="basic-button" type="button" onClick={closeAssignmentDialog}>Annuler</button>
          <button
            className="basic-button active"
            type="button"
            disabled={!assignmentDialog?.assignedUserId}
            onClick={confirmOperationalAssignment}
          >
            {getOperationalAssigneeValue(assignmentDialog?.row) ? 'Réaffecter et mettre à jour la tâche' : 'Confier et créer la tâche'}
          </button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(lineDialog)} onClose={closeLineDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {lineDialog?.mode === 'create' ? 'Ajouter un livrable' : 'Modifier le livrable'}
        </DialogTitle>
        <DialogContent>
          {lineDialog && (
            <div className="lda-line-form">
              <label>
                Dénomination
                <input
                  className="lda-inline-input"
                  value={lineDialog.draft.title}
                  onChange={(event) => updateLineDraft('title', event.target.value)}
                />
              </label>
              <label>
                Emetteur
                <input
                  className="lda-inline-input"
                  value={lineDialog.draft.emitter}
                  onChange={(event) => updateLineDraft('emitter', event.target.value)}
                />
              </label>
              <label>
                Phase
                <input
                  className="lda-inline-input"
                  value={lineDialog.draft.phase}
                  onChange={(event) => updateLineDraft('phase', event.target.value)}
                />
              </label>
              <label>
                N° projet
                <input
                  className="lda-inline-input"
                  value={lineDialog.draft.projectNumber}
                  onChange={(event) => updateLineDraft('projectNumber', event.target.value)}
                />
              </label>
              <label>
                Lot
                <input
                  className="lda-inline-input"
                  value={lineDialog.draft.lot}
                  onChange={(event) => updateLineDraft('lot', event.target.value)}
                />
              </label>
              <label>
                Type document
                <input
                  className="lda-inline-input"
                  value={lineDialog.draft.documentType}
                  onChange={(event) => updateLineDraft('documentType', event.target.value)}
                />
              </label>
              <label>
                N° document
                <input
                  className="lda-inline-input"
                  value={lineDialog.draft.documentNumber}
                  onChange={(event) => updateLineDraft('documentNumber', event.target.value)}
                />
              </label>
              <label>
                Indice
                <input
                  className="lda-inline-input"
                  value={lineDialog.draft.indice}
                  onChange={(event) => updateLineDraft('indice', event.target.value)}
                />
              </label>
              <label>
                Statut
                <select
                  className="lda-inline-select"
                  value={lineDialog.draft.status}
                  onChange={(event) => updateLineDraft('status', event.target.value)}
                >
                  {getLineStatusOptions(lineDialog.draft.status).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Temps de réalisation
                <input
                  className="lda-inline-input"
                  value={lineDialog.draft.realizationTime}
                  onChange={(event) => updateLineDraft('realizationTime', event.target.value)}
                />
              </label>
              <label>
                Début prévu
                <input
                  className="lda-inline-input"
                  type="date"
                  value={lineDialog.draft.forecastStartDate}
                  onChange={(event) => updateLineDraft('forecastStartDate', event.target.value)}
                />
              </label>
              <label>
                Livraison prévue
                <input
                  className="lda-inline-input"
                  type="date"
                  value={lineDialog.draft.deliveryDate}
                  onChange={(event) => updateLineDraft('deliveryDate', event.target.value)}
                />
              </label>

              <label>
                Ressource prévisionnelle
                <select
                  className="lda-inline-select"
                  value={lineDialog.draft.plannedResourceUserId || ''}
                  onChange={(event) => {
                    const userId = event.target.value;
                    const user = users.find((candidate) => String(candidate.id) === String(userId));
                    setLineDialog((current) => current ? {
                      ...current,
                      draft: {
                        ...current.draft,
                        plannedResourceUserId: userId,
                        plannedResourceName: user ? getUserLabel(user) : '',
                        plannedResourceManual: true,
                      },
                    } : current);
                  }}
                >
                  <option value="">Aucune ressource</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {getUserLabel(user)}
                    </option>
                  ))}
                </select>
                <small>Cette ressource est indicative et ne crée aucune tâche.</small>
              </label>
              <label className="lda-line-form__wide">
                Observations
                <input
                  className="lda-inline-input"
                  value={lineDialog.draft.observations}
                  onChange={(event) => updateLineDraft('observations', event.target.value)}
                />
              </label>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <button className="basic-button" type="button" onClick={closeLineDialog}>
            Annuler
          </button>
          <button className="basic-button active" type="button" onClick={saveLineDialog}>
            Enregistrer
          </button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(pendingStatusChange)} onClose={rejectStatusChange}>
        <DialogTitle>Autocontroles</DialogTitle>
        <DialogContent>
          Avez-vous realisé vos autocontroles ?
        </DialogContent>
        <DialogActions>
          <button className="basic-button" type="button" onClick={rejectStatusChange}>
            Non
          </button>
          <button className="basic-button active" type="button" onClick={confirmStatusChange}>
            Oui
          </button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default LdaPage;
